import { useState, useEffect } from 'react';
import { Form, Card, Row, Col, Button, Image } from 'react-bootstrap';
import icValidationError from './assets/img/ic-danger.svg';
import icQ from './assets/img/ic-q.svg';
import icA from './assets/img/ic-a.svg';
import worldLanguages from './data/worldLanguages'
import Select from 'react-select';

const CountrySocial = ({ formData, handleChange, isEditPage, handleGeneralSubmit, validateURL, onValidationError  }) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // ✅ If adding, fields are enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // ✅ Store initial values// ✅ Enable editing mode

    // ✅ Local validation state for social media URLs
    const [socialMediaTouched, setSocialMediaTouched] = useState([]);   // array of booleans per row
    const [socialMediaErrors, setSocialMediaErrors] = useState([]);     // array of error strings per row

    // ✅ Validator – only validate if user typed something
    const validateSocialUrl = (v) =>
        !v?.trim()
            ? '' // empty is allowed; change if you want required
            : (validateURL(v) ? '' : 'Please enter a valid URL.');

    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // ✅ Store previous values
    };
    
    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        // ✅ Social media URLs
        const socialUrls = Array.isArray(formData.socialMediaPlatforms)
            ? formData.socialMediaPlatforms
            : [];

        const socialErrorsArr = socialUrls.map((url) => validateSocialUrl(url));
        const socialTouchedArr = socialUrls.map(() => true);

        setSocialMediaTouched(socialTouchedArr);
        setSocialMediaErrors(socialErrorsArr);

        // ✅ Job platforms URLs
        const jobUrls = Array.isArray(formData.jobPlatforms)
            ? formData.jobPlatforms
            : [];

        const jobErrorsArr = jobUrls.map((url) => validateSocialUrl(url));
        const jobTouchedArr = jobUrls.map(() => true);

        setJobPlatformsTouched(jobTouchedArr);
        setJobPlatformsErrors(jobErrorsArr);

        const hasInvalidSocial = socialErrorsArr.some((msg) => !!msg);
        const hasInvalidJob = jobErrorsArr.some((msg) => !!msg);

        if (hasInvalidSocial || hasInvalidJob) {
            // ❌ At least one URL is invalid – prevent submit
            if (typeof onValidationError === 'function') {
                onValidationError();   // ✅ show parent alert + scroll top
            }
            return;
        }

        // ✅ Strip empty URLs before sending to backend (social)
        const nonEmptySocial = socialUrls.filter((u) => u && u.trim() !== '');
        if (nonEmptySocial.length !== socialUrls.length) {
            handleChange({
                target: {
                    name: 'socialMediaPlatforms',
                    value: nonEmptySocial,
                },
            });
        }

        // ✅ Strip empty URLs before sending to backend (job platforms)
        const nonEmptyJobs = jobUrls.filter((u) => u && u.trim() !== '');
        if (nonEmptyJobs.length !== jobUrls.length) {
            handleChange({
                target: {
                    name: 'jobPlatforms',
                    value: nonEmptyJobs,
                },
            });
        }

        // ✅ All good – proceed with save
        if (typeof handleGeneralSubmit === 'function') {
            await handleGeneralSubmit(e);
            setIsEditing(false);
        } else {
            console.error('Error: handleGeneralSubmit is not a function.');
        }
    };
    
    // ✅ Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        Object.keys(tempFormData).forEach((key) => {
            handleChange({ target: { name: key, value: tempFormData[key] } });
        });
    };

    // ✅ Helper to compare arrays ignoring order
    const arraysEqualIgnoreOrder = (a = [], b = []) => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, idx) => val === sortedB[idx]);
    };

    // ✅ National languages from formData (loaded country)
    const nationalLanguages = Array.isArray(formData.languagesSpoken)
        ? formData.languagesSpoken
        : [];

    // ✅ Dynamic label: show actual languages from DB, fallback to generic text
    const nationalLanguagesLabel = nationalLanguages.length
        ? nationalLanguages.join(', ')
        : 'National language(s)';

    // ✅ National languages + English (deduped)
    const plusEnglish = Array.from(new Set([...nationalLanguages, 'English']));

    // ✅ Local state: which radio is active
    const [languageMode, setLanguageMode] = useState('custom');

    const [hasTouchedLanguageMode, setHasTouchedLanguageMode] = useState(false);
    
    useEffect(() => {
        const current = Array.isArray(formData.importantLanguages)
            ? formData.importantLanguages
            : [];

        // 🛑 Once user picked a mode manually, don't auto-override it
        if (hasTouchedLanguageMode) {
            return;
        }

        if (arraysEqualIgnoreOrder(current, nationalLanguages)) {
            setLanguageMode('national');      // option 1
        } else if (arraysEqualIgnoreOrder(current, plusEnglish)) {
            setLanguageMode('nat_en');        // option 2
        } else {
            setLanguageMode('custom');        // option 3
        }
    }, [formData.importantLanguages, nationalLanguages, plusEnglish, hasTouchedLanguageMode]);

    // ✅ When radio changes, update mode + importantLanguages
    const applyLanguageMode = (mode) => {
        setLanguageMode(mode);
        setHasTouchedLanguageMode(true); // ✅ user explicitly changed mode

        if (mode === 'national') {
            handleChange({
                target: {
                    name: 'importantLanguages',
                    value: nationalLanguages,
                },
            });
        } else if (mode === 'nat_en') {
            handleChange({
                target: {
                    name: 'importantLanguages',
                    value: plusEnglish,
                },
            });
        } else if (mode === 'custom') {
            const current = Array.isArray(formData.importantLanguages)
                ? formData.importantLanguages
                : [];

            const base = plusEnglish; // national + English are always present
            const merged = Array.from(new Set([...base, ...current]));

            handleChange({
                target: {
                    name: 'importantLanguages',
                    value: merged,
                },
            });
        }
    };

    const languageOptions = worldLanguages.map(language => ({ value: language, label: language }));

    // ✅ Normalise social media array from formData
    const socialMediaPlatforms = Array.isArray(formData.socialMediaPlatforms)
        ? formData.socialMediaPlatforms
        : [];

    const handleSocialMediaChange = (index, value) => {
        const updated = [...socialMediaPlatforms];
        updated[index] = value;

        // live-validate if this row was already touched
        setSocialMediaErrors(prev => {
            const next = [...prev];
            if (socialMediaTouched[index]) {
                next[index] = validateSocialUrl(value);
            }
            return next;
        });

        handleChange({
            target: {
                name: 'socialMediaPlatforms',
                value: updated,
            },
        });
    };

    const handleSocialMediaBlur = (index, value) => {
        setSocialMediaTouched(prev => {
            const next = [...prev];
            next[index] = true;
            return next;
        });

        setSocialMediaErrors(prev => {
            const next = [...prev];
            next[index] = validateSocialUrl(value);
            return next;
        });
    };

    const addSocialMediaRow = () => {
        handleChange({
            target: {
                name: 'socialMediaPlatforms',
                value: [...socialMediaPlatforms, ''],
            },
        });
    };

    const removeSocialMediaRow = (index) => {
        const updated = socialMediaPlatforms.filter((_, i) => i !== index);
        handleChange({
            target: {
                name: 'socialMediaPlatforms',
                value: updated.length ? updated : [''],
            },
        });

        // keep touched/errors arrays in sync
        setSocialMediaTouched(prev => prev.filter((_, i) => i !== index));
        setSocialMediaErrors(prev => prev.filter((_, i) => i !== index));
    };

    // ✅ Normalise job platforms array from formData
    const jobPlatforms = Array.isArray(formData.jobPlatforms)
        ? formData.jobPlatforms
        : [];

    const [jobPlatformsTouched, setJobPlatformsTouched] = useState([]);   // array of booleans per row
    const [jobPlatformsErrors, setJobPlatformsErrors] = useState([]);     // array of error strings per row

    const handleJobPlatformChange = (index, value) => {
        const updated = [...jobPlatforms];
        updated[index] = value;

        // live-validate if this row was already touched
        setJobPlatformsErrors(prev => {
            const next = [...prev];
            if (jobPlatformsTouched[index]) {
                next[index] = validateSocialUrl(value); // ✅ reuse same URL validator
            }
            return next;
        });

        handleChange({
            target: {
                name: 'jobPlatforms',
                value: updated,
            },
        });
    };

    const handleJobPlatformBlur = (index, value) => {
        setJobPlatformsTouched(prev => {
            const next = [...prev];
            next[index] = true;
            return next;
        });

        setJobPlatformsErrors(prev => {
            const next = [...prev];
            next[index] = validateSocialUrl(value);
            return next;
        });
    };

    const addJobPlatformRow = () => {
        handleChange({
            target: {
                name: 'jobPlatforms',
                value: [...jobPlatforms, ''],
            },
        });
    };

    const removeJobPlatformRow = (index) => {
        const updated = jobPlatforms.filter((_, i) => i !== index);
        handleChange({
            target: {
                name: 'jobPlatforms',
                value: updated.length ? updated : [''],
            },
        });

        // keep touched/errors arrays in sync
        setJobPlatformsTouched(prev => prev.filter((_, i) => i !== index));
        setJobPlatformsErrors(prev => prev.filter((_, i) => i !== index));
    };



    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row className="align-items-center">
                        <Col>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3><span className="item-number">6. </span>Social</h3>
                            <hr />
                        </Col>
                    </Row>
                    {isEditing ? (
                        <>
                            <Row>
                                <Col md={6}> 
                                    {/*  Most important languages */}
                                    <Form.Group controlId="importantLanguages">
                                        <Form.Label>
                                            Which languages can be used in the work and social environment?
                                        </Form.Label>

                                        {/* ✅ Radio group for language mode */}
                                        <div className="mb-2">
                                            <Form.Check
                                                type="radio"
                                                id="lang-mode-national"
                                                name="importantLanguagesMode"
                                                label={`${nationalLanguagesLabel} (from general DB)`}
                                                value="national"
                                                checked={languageMode === 'national'}
                                                onChange={() => applyLanguageMode('national')}
                                                disabled={!isEditing}
                                            />

                                            <Form.Check
                                                type="radio"
                                                id="lang-mode-nat-en"
                                                name="importantLanguagesMode"
                                                label={`${nationalLanguagesLabel} + English`}
                                                value="nat_en"
                                                checked={languageMode === 'nat_en'}
                                                onChange={() => applyLanguageMode('nat_en')}
                                                disabled={!isEditing}
                                            />

                                            <Form.Check
                                                type="radio"
                                                id="lang-mode-custom"
                                                name="importantLanguagesMode"
                                                label={`${nationalLanguagesLabel} + English + other languages`}
                                                value="custom"
                                                checked={languageMode === 'custom'}
                                                onChange={() => applyLanguageMode('custom')}
                                                disabled={!isEditing}
                                            />
                                        </div>

                                        {languageMode === 'custom' && (
                                            <Select
                                                options={Array.isArray(languageOptions) ? languageOptions : []}
                                                value={
                                                    (Array.isArray(formData.importantLanguages)
                                                        ? formData.importantLanguages
                                                        : []
                                                    )
                                                        .map(val =>
                                                            (languageOptions || []).find(
                                                                opt => String(opt.value) === String(val)
                                                            )
                                                        )
                                                        .filter(Boolean)
                                                }
                                                onChange={(selected) => {
                                                    const extras = Array.isArray(selected)
                                                        ? selected.map(s => s.value)
                                                        : [];

                                                    const base = plusEnglish; // national + English
                                                    const next = Array.from(new Set([...base, ...extras]));

                                                    handleChange({
                                                        target: {
                                                            name: 'importantLanguages',
                                                            value: next,
                                                        },
                                                    });
                                                }}
                                                isMulti
                                                isClearable={false}
                                                isDisabled={!isEditing}
                                                className="react-select"
                                                classNamePrefix="react-select"
                                                placeholder="Add other languages…"
                                            />
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}> 
                                    {/* Social media platforms (multiple URLs) */}
                                    <Form.Group className="validated" controlId="socialMediaPlatforms">
                                        <Form.Label>What are the most common social media platforms?</Form.Label>

                                        {(socialMediaPlatforms.length ? socialMediaPlatforms : ['']).map((url, index) => {
                                            const hasError = !!(socialMediaTouched[index] && socialMediaErrors[index]);

                                            return (
                                                <div key={index} className="mb-2 position-relative">
                                                    <div className="d-flex gap-2">
                                                        <Form.Control
                                                            type="url"
                                                            name={`socialMediaPlatforms-${index}`}
                                                            value={url || ''}
                                                            placeholder="example.com"
                                                            onChange={(e) => handleSocialMediaChange(index, e.target.value)}
                                                            onBlur={(e) => handleSocialMediaBlur(index, e.target.value)}
                                                            disabled={!isEditing}
                                                            isInvalid={hasError}
                                                            aria-invalid={hasError}
                                                            inputMode="url"
                                                        />
                                                    </div>

                                                    {/* ✅ Same error icon + tooltip style as Country URL */}
                                                    {hasError && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={socialMediaErrors[index]}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">
                                                                {socialMediaErrors[index]}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {/* 🔻 Single "Remove platform" link under fields, above "Add" */}
                                        {isEditing && socialMediaPlatforms.length > 1 && (
                                            <Row>
                                                <Col className="text-end">
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        onClick={() => removeSocialMediaRow(socialMediaPlatforms.length - 1)}
                                                        className="align-center text-danger p-0"
                                                    >
                                                        Remove platform
                                                    </Button>
                                                </Col>
                                            </Row>
                                        )}

                                        {isEditing && (
                                            <Button
                                                variant="primary"
                                                className="btn-sm"
                                                onClick={addSocialMediaRow}
                                            >
                                                + Add another platform
                                            </Button>
                                        )}
                                    </Form.Group>
                                </Col>

                                <Col md={6} className="pt-4"> 
                                    {/* Job platforms (multiple URLs) */}
                                    <Form.Group className="validated" controlId="jobPlatforms">
                                        <Form.Label>What are the most common national job search sites?</Form.Label>

                                        {(jobPlatforms.length ? jobPlatforms : ['']).map((url, index) => {
                                            const hasError = !!(jobPlatformsTouched[index] && jobPlatformsErrors[index]);

                                            return (
                                                <div key={index} className="mb-2 position-relative">
                                                    <div className="d-flex gap-2">
                                                        <Form.Control
                                                            type="url"
                                                            name={`jobPlatforms-${index}`}
                                                            value={url || ''}
                                                            placeholder="example.com"
                                                            onChange={(e) => handleJobPlatformChange(index, e.target.value)}
                                                            onBlur={(e) => handleJobPlatformBlur(index, e.target.value)}
                                                            disabled={!isEditing}
                                                            isInvalid={hasError}
                                                            aria-invalid={hasError}
                                                            inputMode="url"
                                                        />
                                                    </div>

                                                    {/* ✅ Same error icon + tooltip style as Country URL */}
                                                    {hasError && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={jobPlatformsErrors[index]}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">
                                                                {jobPlatformsErrors[index]}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {/* 🔻 Single "Remove platform" link under fields, above "Add" */}
                                        {isEditing && jobPlatforms.length > 1 && (
                                            <Row>
                                                <Col className="text-end">
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        onClick={() => removeJobPlatformRow(jobPlatforms.length - 1)}
                                                        className="align-center text-danger p-0"
                                                    >
                                                        Remove platform
                                                    </Button>
                                                </Col>
                                            </Row>
                                        )}

                                        {isEditing && (
                                            <Button
                                                variant="primary"
                                                className="btn-sm"
                                                onClick={addJobPlatformRow}
                                            >
                                                + Add another platform
                                            </Button>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="Which languages are most important in the work and social environment?" />
                                                </span>
                                                <span className="text">
                                                    Which languages are most important in the work and social environment?
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="Which languages are most important in the work and social environment?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {Array.isArray(formData.importantLanguages) && formData.importantLanguages.length > 0
                                                                    ? formData.importantLanguages.join(', ')
                                                                    : 'N/A'
                                                                }
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="What are the most common social media platforms?"/>
                                                </span>
                                                <span className="text">
                                                    What are the most common social media platforms?
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="What are the most common social media platforms?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {Array.isArray(formData.socialMediaPlatforms) && formData.socialMediaPlatforms.length > 0 ? (
                                                                    formData.socialMediaPlatforms.map((url, index) => {
                                                                        const normalizedUrl =
                                                                            url && /^https?:\/\//i.test(url)
                                                                                ? url
                                                                                : `https://${url}`; // ✅ add https:// if missing

                                                                        return (
                                                                            <span key={index}>
                                                                                <a
                                                                                    href={normalizedUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                >
                                                                                    {url}
                                                                                </a>
                                                                                <br />
                                                                            </span>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    'N/A'
                                                                )}
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>

                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="What are the most common national job search sites?"/>
                                                </span>
                                                <span className="text">
                                                    What are the most common national job search sites?
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="What are the most common national job search sites?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {Array.isArray(formData.jobPlatforms) && formData.jobPlatforms.length > 0 ? (
                                                                    formData.jobPlatforms.map((url, index) => {
                                                                        const normalizedUrl =
                                                                            url && /^https?:\/\//i.test(url)
                                                                                ? url
                                                                                : `https://${url}`; // ✅ add https:// if missing

                                                                        return (
                                                                            <span key={index}>
                                                                                <a
                                                                                    href={normalizedUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                >
                                                                                    {url}
                                                                                </a>
                                                                                <br />
                                                                            </span>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    'N/A'
                                                                )}
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default CountrySocial;
